
import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { SidebarGroup, SidebarGroupContent } from "@/components/ui/sidebar/components/SidebarGroup";
import { cn } from "@/lib/utils";
import GridCardDeleteDialog from "@/components/dashboard/content-card/GridCardDeleteDialog";
import { toast } from "sonner";
import { useContentOperations } from "@/hooks/useContentOperations";
import { useQueryClient } from "@tanstack/react-query";

interface DragData {
  contentId?: string;
  contentIds?: string[];
  contentTitle?: string;
  contentTitles?: string;
  currentCollectionId?: string;
  isMultiDrag?: boolean;
}

const SidebarDeleteContentSection = () => {
  // State for tracking when content is being dragged over this section
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<{ 
    ids: string[]; 
    title: string; 
    isMultiple: boolean;
  } | null>(null);
  const { handleDelete } = useContentOperations();
  const queryClient = useQueryClient();
  
  // Handle drag events for visual feedback
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDropTarget(true);
  };
  
  const handleDragLeave = () => {
    setIsDropTarget(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDropTarget(false);
    
    try {
      // Try to extract the drag data
      const jsonData = e.dataTransfer.getData('application/json');
      
      if (!jsonData) {
        console.error('No valid JSON data found in drag event');
        toast.error("Couldn't process the dropped item");
        return;
      }
      
      const data: DragData = JSON.parse(jsonData);
      
      // Handle multi-drag case
      if (data.isMultiDrag && data.contentIds && data.contentIds.length > 0) {
        console.log(`Multiple items dropped for deletion: ${data.contentIds.length} items`);
        
        // Set the content to delete and show the confirmation dialog
        setContentToDelete({
          ids: data.contentIds,
          title: `${data.contentIds.length} items`,
          isMultiple: true
        });
        setShowDeleteDialog(true);
        return;
      }
      
      // Single item case
      if (!data.contentId || !data.contentTitle) {
        console.error('Invalid content data in drag operation');
        toast.error("Missing content information");
        return;
      }
      
      console.log(`Content dropped for deletion: ${data.contentTitle} (${data.contentId})`);
      
      // Set the content to delete and show the confirmation dialog
      setContentToDelete({
        ids: [data.contentId],
        title: data.contentTitle,
        isMultiple: false
      });
      setShowDeleteDialog(true);
      
    } catch (error) {
      console.error('Error processing dropped content:', error);
      toast.error("Failed to process drag and drop operation");
    }
  };

  const handleConfirmDelete = () => {
    if (contentToDelete) {
      // Delete each content item
      contentToDelete.ids.forEach(id => {
        handleDelete(id);
      });
      
      // Invalidate queries to refresh all content displays
      queryClient.invalidateQueries({ queryKey: ['contentItems'] });
      queryClient.invalidateQueries({ queryKey: ['collections'] });
      queryClient.invalidateQueries({ queryKey: ['contentCount'] });
      
      if (contentToDelete.isMultiple) {
        toast.success(`${contentToDelete.title} deleted successfully`);
      } else {
        toast.success(`"${contentToDelete.title}" deleted successfully`);
      }
    }
  };

  return (
    <>
      <SidebarGroup className="border border-border rounded-md p-0">
        <SidebarGroupContent className="py-0">
          <div 
            className={cn(
              "flex items-center px-3 py-0 w-full h-8 text-sm hover:bg-accent/50 transition-colors rounded-md",
              isDropTarget && "bg-accent"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="h-5 w-5 mr-3 flex items-center justify-center text-red-500">
              <Trash2 size={18} />
            </div>
            <span>Delete Content</span>
          </div>
        </SidebarGroupContent>
      </SidebarGroup>

      {contentToDelete && (
        <GridCardDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          itemTitle={contentToDelete.isMultiple ? `${contentToDelete.title}` : contentToDelete.title}
          onConfirm={handleConfirmDelete}
          isMultiDelete={contentToDelete.isMultiple}
        />
      )}
    </>
  );
};

export default SidebarDeleteContentSection;
